import { useDebounceFn } from '@vueuse/core';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { GaiaEventType, WebsocketSubscriptionTarget, type GaiaEvent } from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { logger } from '@/utils/logger';

/** The event each signing channel carries — both are bare "refetch" signals. */
const EVENT_FOR_TARGET = {
  [WebsocketSubscriptionTarget.SIGNING_REQUEST]: GaiaEventType.SIGNING_REQUEST_UPDATED,
  [WebsocketSubscriptionTarget.SIGNING_REQUESTS]: GaiaEventType.SIGNING_REQUESTS_UPDATED,
} as const;

export interface UseSigningRequestWebSocketOptions {
  /**
   * `SIGNING_REQUEST` watches one request (uid = the request's); `SIGNING_REQUESTS`
   * watches the organisation's list (uid = the organisation's).
   */
  target: keyof typeof EVENT_FOR_TARGET;
  /** What to subscribe to; followed live, null subscribes to nothing. */
  uid: () => string | null;
  /** Something changed — refetch. Debounced: one change can arrive as a burst. */
  onUpdated: () => void;
}

function eventUid(event: GaiaEvent): string | null {
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null || !('uid' in data)) return null;
  return typeof data.uid === 'string' ? data.uid : null;
}

/**
 * Keep a signing request — or the organisation's list of them — in sync with the
 * backend. Sending a request only queues its hand-off to the provider; the worker
 * moves it out of draft seconds later, and signers sign whenever they do, so
 * neither page can know when to look again without this.
 */
export function useSigningRequestWebSocket(options: UseSigningRequestWebSocketOptions): void {
  const eventType = EVENT_FOR_TARGET[options.target];
  const refetch = useDebounceFn(() => {
    options.onUpdated();
  }, 300);
  let subscribedUid: string | null = null;
  let unsubscribe: (() => void) | null = null;
  const disposers: (() => void)[] = [];

  function follow(uid: string | null): void {
    if (uid === subscribedUid) return;
    unsubscribe?.();
    unsubscribe = uid ? getWsClient().addSubscriptionUid(options.target, uid) : null;
    subscribedUid = uid;
  }

  // The shared socket fans every event out to every mounted caller; keep ours.
  const handler: WebSocketEventHandler = event => {
    if (event.gaia_event_type !== eventType) return;
    if (subscribedUid && eventUid(event) === subscribedUid) void refetch();
  };

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }
    disposers.push(
      getWsClient().registerHandler(options.target, handler),
      watch(options.uid, follow, { immediate: true })
    );
  });

  onBeforeUnmount(() => {
    follow(null);
    for (const dispose of disposers) dispose();
    disposers.length = 0;
  });
}
